/**
 * Book & Hold: shared formatting for a held fare's ticketing deadline. It is an absolute UTC instant, so
 * durations are a plain `deadline - now` diff and the absolute time renders in the viewer's own timezone.
 */

/**
 * Short duration until the deadline, coarsest units only ("3d 6h", "6h 12m", "18m"). Returns `undefined`
 * if the deadline is invalid or already passed.
 */
export function formatHoldDuration(deadline: string): string | undefined {
  const totalMinutes = Math.floor((new Date(deadline).getTime() - Date.now()) / 60000)
  if (!Number.isFinite(totalMinutes) || totalMinutes <= 0) {
    return undefined
  }
  const days = Math.floor(totalMinutes / 1440)
  const hours = Math.floor((totalMinutes % 1440) / 60)
  const minutes = totalMinutes % 60
  if (days > 0) {
    return hours > 0 ? `${days}d ${hours}h` : `${days}d`
  }
  if (hours > 0) {
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`
  }
  return `${minutes}m`
}

/**
 * The held fare's ticketing deadline off a trip request's items. Only flight items are ever held, and a
 * request holds at most one fare, so the first flight item carrying a deadline wins.
 */
export function getTicketingDeadline(items?: Array<App.BusinessTraveller.TripRequestItem>): string | undefined {
  return items?.find(item => item.type === 'flight' && item.details?.ticketingDeadline)?.details?.ticketingDeadline
}

/** True while a held fare's ticketing deadline is still in the future; false when absent, unparseable or passed. */
export function isHoldReservationStillActive(deadline: string | undefined, now: number = Date.now()): boolean {
  const deadlineMs = deadline ? new Date(deadline).getTime() : 0
  return Number.isFinite(deadlineMs) && deadlineMs > now
}

/**
 * Live countdown to the deadline in unit-suffixed form ("3d 8h 3m 21s", "3m 21s", "21s"), dropping larger
 * units once they empty. `undefined` once invalid or passed.
 */
export function formatHoldCountdown(deadline: string): string | undefined {
  const totalSeconds = Math.floor((new Date(deadline).getTime() - Date.now()) / 1000)
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) {
    return undefined
  }
  const days = Math.floor(totalSeconds / 86400)
  const hours = Math.floor((totalSeconds % 86400) / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const parts: Array<string> = []
  if (days > 0) {
    parts.push(`${days}d`)
  }
  if (days > 0 || hours > 0) {
    parts.push(`${hours}h`)
  }
  if (days > 0 || hours > 0 || minutes > 0) {
    parts.push(`${minutes}m`)
  }
  parts.push(`${totalSeconds % 60}s`)
  return parts.join(' ')
}

/** Urgency tier of a held fare, for colouring the hold notice as the deadline nears. */
export type HoldUrgency = 'normal' | 'soon' | 'urgent'

/**
 * Urgency tier from time left: `normal` (≥1 day), `soon` (<1 day), `urgent` (<1 hour). An invalid deadline
 * falls back to `normal`; callers pass only future deadlines (expiry is handled separately).
 */
export function holdUrgency(deadline: string): HoldUrgency {
  const minutes = Math.floor((new Date(deadline).getTime() - Date.now()) / 60000)
  if (!Number.isFinite(minutes) || minutes >= 1440) {
    return 'normal'
  }
  return minutes >= 60 ? 'soon' : 'urgent'
}

/** True if the deadline falls on today's date in the viewer's own timezone. */
export function isHoldDeadlineToday(deadline: string): boolean {
  return new Date(deadline).toDateString() === new Date().toDateString()
}

/**
 * The deadline as a wall-clock time in the viewer's own timezone, dropping the date when it's today
 * ("10:00 pm" vs "22/02/2026, 10:00 pm").
 */
export function formatHoldDeadline(deadline: string): string {
  const dateParts = isHoldDeadlineToday(deadline)
    ? {}
    : { day: '2-digit' as const, month: '2-digit' as const, year: 'numeric' as const }
  const formatted = new Date(deadline).toLocaleString('en-AU', {
    ...dateParts,
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })
  // Keep the meridiem attached to the time so "…03:51 pm" never wraps to an orphaned "pm".
  return formatted.replace(/\s(am|pm)$/i, ' $1')
}
